import { faArrowRight, faChevronLeft, faChevronRight, faClock, faFileAlt, faInfoCircle, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ButtonAtom from "../../../components/atoms/button/button";
import InvoiceStepper from "../../../components/atoms/InvoiceStepper";
import ProjectHeader from "../../../components/ProjectHeader";
import useProjectPermission from "../../../hooks/useProjectPermission";
import INVOICE_STATUS from "../../../constants/invoice-status";
import {
    completeStep,
    createInvoiceV2,
    fetchInvoiceByIdV2,
    fetchLVsByTicketCategory,
    fetchPreviousInvoicePositionsV2,
    fetchPreviousInvoicesV2,
    navigateToStep,
    performFormValidation,
    resetForm,
    selectChangeTrackingState,
    selectCompletedSteps,
    selectCurrentStep,
    selectDisabledStepsMap,
    selectExistingDraftInvoice,
    selectFormState,
    selectInvoicePositions,
    selectLVData,
    selectMaxStepReached,
    selectPreviousInvoices,
    selectSkipHistoricalStep,
    setFormField,
    setHistoricalDataConfirmed,
    setSkipHistoricalStep,
    updateInvoiceV2,
    validateForm,
} from "../../../store/slices/invoiceV2Slice";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import {
    getInvoiceV2ValidationToastKey,
    prepareInvoiceV2DataForSubmission,
} from "../../../utils/invoiceV2Utils";
import { showToast } from "../../../utils/method";
import "./add-invoice-v2.scss";
import HierarchicalPositions from "./components/HierarchicalPositions";
import InvoiceDetailFieldsV2 from "./components/InvoiceDetailFieldsV2";
import InvoiceModals from "./components/InvoiceModals";
import InvoiceSequenceConfiguration from "./components/InvoiceSequenceConfiguration";
import InvoiceSummary from "./components/InvoiceSummary";
import InvoiceSummaryInfoBar from "./components/InvoiceSummaryInfoBar";
import TicketCategorySelection from "./components/TicketCategorySelection";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

function AddInvoiceV2({ mode = "add" }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { projectId: projectIdParam, invoiceId } = useParams();

    // Stepper state from Redux
    const currentStep = useSelector(selectCurrentStep);
    const completedSteps = useSelector(selectCompletedSteps);
    const skipHistoricalStep = useSelector(selectSkipHistoricalStep);
    const maxStepReached = useSelector(selectMaxStepReached);
    const disabledStepsMap = useSelector(selectDisabledStepsMap);

    // State for loading in edit/view modes
    const [isLoading, setIsLoading] = useState(false);

    // State for historical data confirmation modal
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // State for historical invoices modal
    const [showHistoricalInvoicesModal, setShowHistoricalInvoicesModal] =
        useState(false);

    // State for previous invoices modal (for selected ticket category)
    const [showPreviousInvoicesModal, setShowPreviousInvoicesModal] =
        useState(false);

    // State for previous invoice's historical data modal
    const [showPreviousInvoiceHistoricalModal, setShowPreviousInvoiceHistoricalModal] =
        useState(false);
    const [selectedPreviousInvoice, setSelectedPreviousInvoice] = useState(null);

    // Redux state selectors using the new structure
    const projectDetails = useSelector(selectProjectDetails);
    // Get the projectId either from params or from Redux store
    const { id: projectIdFromStore } = projectDetails || {};
    const projectId = projectIdParam || projectIdFromStore;
    const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
        { projectId },
        { skip: !projectId },
    );
    const ticketCategories = useMemo(
        () => categoriesResponse?.items ?? [],
        [categoriesResponse],
    );
    const formValues = useSelector(selectFormState);
    const lvData = useSelector(selectLVData);
    const invoicePositions = useSelector(selectInvoicePositions);
    const previousInvoices = useSelector(selectPreviousInvoices);
    const existingDraftInvoice = useSelector(selectExistingDraftInvoice);

    // Use combined selectors for better performance
    const changeTracking = useSelector(selectChangeTrackingState);

    // RBAC check (ACCESS_RIGHT_AREAS.INVOICE_AUFMASS) ignored project grants.
    const { can } = useProjectPermission();
    const canManageAufmass = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.MANAGE_AUFMASS);

    // Calculate last autosave time for display
    const lastAutosaveTime = changeTracking.lastAutosaved
        ? new Date(changeTracking.lastAutosaved).toLocaleTimeString()
        : null;

    // Check for incomplete invoice (any invoice that is not FINALIZED or REJECTED)
    const hasIncompleteInvoice = useMemo(() => {
        return (
            existingDraftInvoice ||
            previousInvoices.some(
                (invoice) =>
                    invoice.status !== INVOICE_STATUS.REJECTED &&
                    invoice.status !== INVOICE_STATUS.FINALIZED
            )
        );
    }, [existingDraftInvoice, previousInvoices]);

    // Fetch invoice data for edit and view modes
    useEffect(() => {
        if (["edit", "view"].includes(mode) && invoiceId) {
            setIsLoading(true);
            dispatch(fetchInvoiceByIdV2(invoiceId))
                .unwrap()
                .then((response) => {
                    // In edit/view modes, set all steps as completed and navigate to Step 3
                    dispatch(completeStep(1));
                    dispatch(completeStep(2));
                    dispatch(completeStep(3));
                    dispatch(navigateToStep(3));

                    // Fetch previous invoices for the ticket category to show in summary bar
                    if (response.invoice?.projectTicketCategoryId) {
                        dispatch(
                            fetchPreviousInvoicesV2({
                                projectId: projectId,
                                ticketCategoryId:
                                    response.invoice.projectTicketCategoryId,
                                currentInvoiceId: response.invoice.id,
                            }),
                        );
                    }
                })
                .catch((error) => {
                    console.error("Error fetching invoice:", error);
                    showToast(t("invoice.error-load-failed"), {
                        type: "error",
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [dispatch, invoiceId, mode, t, projectId]);

    // Set project ID in Redux state
    useEffect(() => {
        dispatch(
            setFormField({
                field: "projectId",
                value: projectId,
                skipDirtyFlag: true,
            }),
        );
    }, [dispatch, projectId]);

    // Memoized function to handle completion of ticket category selection
    const handleCategorySelectionComplete = useCallback(async () => {
        // Get the selected ticket category from Redux state
        const selectedTicketCategory = formValues?.selectedTicketCategory;

        if (!selectedTicketCategory) {
            showToast(t("invoice.error-ticket-category-required"), {
                type: "error",
            });
            return;
        }

        try {
            // Fetch previous invoices to determine if we should skip Step 2
            const previousInvoices = await dispatch(
                fetchPreviousInvoicesV2({
                    projectId: projectId,
                    ticketCategoryId: selectedTicketCategory.value,
                }),
            ).unwrap();

            // If there are previous invoices, skip the historical baseline step
            const shouldSkipStep2 =
                previousInvoices && previousInvoices.length > 0;
            dispatch(setSkipHistoricalStep(shouldSkipStep2));

            // Fetch hierarchical LV data for the selected ticket category
            // This now includes ticket category details (VAT, financial settings, etc.)
            await dispatch(
                fetchLVsByTicketCategory({
                    projectId: projectId,
                    ticketCategoryId: selectedTicketCategory.value,
                }),
            ).unwrap();

            // If we're in add mode, try to load positions from previous invoice
            if (mode === "add") {
                await dispatch(
                    fetchPreviousInvoicePositionsV2({
                        projectId: projectId,
                        ticketCategoryId: selectedTicketCategory.value,
                    }),
                );
            }

            // Mark Step 1 as complete
            dispatch(completeStep(1));

            // Navigate to next step
            if (shouldSkipStep2) {
                // Skip Step 2, go directly to Step 3
                dispatch(completeStep(2));
                dispatch(navigateToStep(3));
            } else {
                // Go to Step 2
                dispatch(navigateToStep(2));
            }
        } catch (error) {
            console.error("Error loading ticket category data:", error);
            showToast(t("error.failed-to-load-category-data"), {
                type: "error",
            });
        }
    }, [dispatch, formValues?.selectedTicketCategory, projectId, mode, t]);

    // Build the minimal state slice performFormValidation expects. The
    // ticketCategory details (cashDiscount/cashDiscountDays) are required so
    // the skonto validation can run - without them the cash-discount fields
    // would never be flagged as mandatory.
    const buildValidationState = useCallback(() => {
        const selectedCategory = ticketCategories?.find(
            (cat) => cat.id === formValues?.selectedTicketCategory?.value,
        );
        return {
            invoiceV2: {
                formState: formValues,
                invoicePositions,
                ticketCategory: { details: selectedCategory },
            },
        };
    }, [ticketCategories, formValues, invoicePositions]);

    // Handle historical baseline step completion
    const handleHistoricalBaselineComplete = useCallback(() => {
        // Show confirmation modal before proceeding
        setShowConfirmationModal(true);
    }, []);

    // Confirm historical data and move to next step
    const confirmHistoricalData = useCallback(() => {
        dispatch(setHistoricalDataConfirmed(true));
        dispatch(completeStep(2));
        dispatch(navigateToStep(3));
        setShowConfirmationModal(false);
    }, [dispatch]);

    // Handle viewing historical invoices from previous invoices table
    const handleViewHistoricalInvoices = useCallback((invoice) => {
        setSelectedPreviousInvoice(invoice);
        setShowPreviousInvoiceHistoricalModal(true);
    }, []);

    // Handle closing previous invoice historical modal
    const handleClosePreviousInvoiceHistorical = useCallback(() => {
        setShowPreviousInvoiceHistoricalModal(false);
        setSelectedPreviousInvoice(null);
    }, []);

    // Handle stepper click
    const handleStepperClick = useCallback(
        (stepNumber) => {
            // Prevent navigation if there are unsaved changes
            if (changeTracking.isDirty) {
                showToast(t("invoice.save-before-navigation"), {
                    type: "warning",
                });
                return;
            }

            // Don't allow going to Step 2 if it's skipped
            if (stepNumber === 2 && skipHistoricalStep) {
                return;
            }

            dispatch(navigateToStep(stepNumber));
        },
        [dispatch, changeTracking.isDirty, skipHistoricalStep, t],
    );

    // Handle back button in stepper
    const handleStepBack = useCallback(() => {
        if (currentStep > 1) {
            const prevStep =
                currentStep === 3 && skipHistoricalStep ? 1 : currentStep - 1;
            dispatch(navigateToStep(prevStep));
        }
    }, [currentStep, skipHistoricalStep, dispatch]);

    // Shared submission function - V2 only supports draft saving
    const submitInvoice = useCallback(() => {
        // Use the utility function to prepare the data for submission
        const payloadData = prepareInvoiceV2DataForSubmission(
            formValues,
            invoicePositions,
        );

        let action;
        if (formValues.id) {
            // Update existing invoice
            action = updateInvoiceV2({
                invoiceId: formValues.id,
                invoiceData: payloadData,
            });
        } else {
            // Create new invoice
            action = createInvoiceV2({
                projectId: projectId,
                invoiceData: payloadData,
            });
        }

        // Return the dispatch promise to allow chaining
        return dispatch(action)
            .then((result) => {
                if (result.type.endsWith("/fulfilled")) {
                    const savedInvoice = result.payload;

                    if (!formValues.id && savedInvoice?.id) {
                        dispatch(
                            setFormField({
                                field: "id",
                                value: savedInvoice.id,
                                skipDirtyFlag: true,
                            }),
                        );
                    }

                    return savedInvoice;
                }
                // Error toast already shown in thunk via handleAsyncError
                return null;
            })
            .catch(() => null);
    }, [dispatch, formValues, invoicePositions, projectId]);

    // Handle next button in stepper
    const handleStepNext = useCallback(() => {
        if (currentStep === 1) {
            handleCategorySelectionComplete();
        } else if (currentStep === 2) {
            handleHistoricalBaselineComplete();
        } else if (currentStep === 3) {
            // Save invoice on Step 3
            const { isValid, errors } = performFormValidation(
                buildValidationState(),
            );
            dispatch(validateForm({ force: true }));

            if (isValid) {
                submitInvoice();
            } else {
                showToast(getInvoiceV2ValidationToastKey(errors), {
                    type: "error",
                });
            }
        }
    }, [
        currentStep,
        handleCategorySelectionComplete,
        handleHistoricalBaselineComplete,
        buildValidationState,
        dispatch,
        submitInvoice,
    ]);

    // Define stepper steps
    const stepperSteps = useMemo(() => {
        const steps = [
            { label: "invoice.step-select-category" },
            { label: "invoice.step-historical-baseline" },
            { label: "invoice.step-invoice-details" },
        ];

        // If Step 2 is skipped, filter it out from display
        if (skipHistoricalStep) {
            return steps.filter((_, index) => index !== 1);
        }

        return steps;
    }, [skipHistoricalStep]);

    // Warn user before leaving page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (changeTracking.isDirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [changeTracking.isDirty]);

    useEffect(() => {
        return () => {
            dispatch(resetForm());
        };
    }, [dispatch]);

    // Memoized page title generator
    const pageTitle = useMemo(() => {
        if (mode === "add") {
            if (currentStep === 1) {
                return t("invoice.select-ticket-category");
            } else if (currentStep === 2 && !skipHistoricalStep) {
                return t("invoice.configure-historical-baseline");
            } else if (currentStep === 3) {
                return formValues.invoiceNumber
                    ? `${t("invoice.invoice")} - ${formValues.invoiceNumber}`
                    : t("invoice.new-invoice");
            }
        }

        return formValues.invoiceNumber
            ? `${t("invoice.invoice")} - ${formValues.invoiceNumber}`
            : t("invoice.invoice");
    }, [mode, currentStep, skipHistoricalStep, formValues.invoiceNumber, t]);

    // Left panel (details) collapsed state - auto-collapse on mobile
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(
        typeof window !== "undefined" && window.innerWidth < 768
    );

    // Add loading state for edit/view modes
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                        {t("common.loading")}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="invoice-add-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={pageTitle}
            />

            <div className="page-container">
                {/* Unified toolbar - InvoiceStepper for all modes */}
                <InvoiceStepper
                    currentStep={currentStep}
                    steps={stepperSteps}
                    completedSteps={completedSteps}
                    onStepClick={handleStepperClick}
                    maxStepReached={maxStepReached}
                    onBack={
                        mode === "view"
                            ? null
                            : mode === "add"
                                ? handleStepBack
                                : () => navigate(`/projects/${projectId}/invoice`)
                    }
                    disabledStepsMap={disabledStepsMap}
                    onNext={
                        mode === "add" && currentStep < 3 ? handleStepNext : null
                    }
                    nextDisabled={
                        (currentStep === 1 && (!formValues.selectedTicketCategory || hasIncompleteInvoice)) ||
                        (currentStep === 2 &&
                            formValues.isHistoricalBaseline &&
                            (!formValues.historicalInvoices ||
                                formValues.historicalInvoices.length === 0))
                    }
                    nextLabel={
                        currentStep === (skipHistoricalStep ? 2 : 3)
                            ? t("common.finish")
                            : t("common.next")
                    }
                    hideStepIndicators={mode !== "add"}
                    backLabel={mode === "edit" ? t("common.back") : undefined}
                    actions={
                        currentStep === 3 ? (
                            <>
                                {!formValues.readonly && (
                                    <>
                                        <ButtonAtom
                                            className="btn btn-sm btn-success"
                                            onClick={() => {
                                                const { isValid, errors } =
                                                    performFormValidation(buildValidationState());
                                                dispatch(validateForm({ force: true }));
                                                if (isValid) {
                                                    submitInvoice();
                                                } else {
                                                    showToast(
                                                        getInvoiceV2ValidationToastKey(errors),
                                                        { type: "error" },
                                                    );
                                                }
                                            }}
                                            disabled={formValues.readonly}
                                        >
                                            <FontAwesomeIcon icon={faSave} className="me-1" />
                                            {t("common.save")}
                                        </ButtonAtom>
                                        {canManageAufmass && (
                                            <ButtonAtom
                                                className="btn btn-sm btn-primary"
                                                disabled={!formValues.id || changeTracking.isDirty}
                                                onClick={() => {
                                                    if (!formValues.id || changeTracking.isDirty) {
                                                        showToast(
                                                            t("invoice.error-save-before-aufmass"),
                                                            { type: "warning" },
                                                        );
                                                        return;
                                                    }
                                                    navigate(
                                                        `/projects/${projectId}/invoice/aufmass-v2?invoiceId=${formValues.id}`,
                                                    );
                                                }}
                                            >
                                                {t("invoice.create-aufmass")}
                                                <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                                            </ButtonAtom>
                                        )}
                                    </>
                                )}
                            </>
                        ) : null
                    }
                />

                <div className="invoice-content">
                    {/* Step 1: Ticket Category Selection */}
                    {currentStep === 1 && (
                        <div className="invoice-step1-content p-3">
                            <TicketCategorySelection
                                projectId={projectId}
                                disabled={mode !== "add"}
                            />
                        </div>
                    )}

                    {/* Step 2: Historical Baseline Configuration (conditional) */}
                    {currentStep === 2 && !skipHistoricalStep && (
                        <div className="p-3">
                            <InvoiceSequenceConfiguration mode={mode} />
                        </div>
                    )}

                    {/* Step 3: two-column layout - details panel + positions center stage */}
                    {currentStep === 3 && (
                        <div className="invoice-step3-layout">
                            {/* Left details + summary panel */}
                            <div className={`step3-left-panel${isPanelCollapsed ? " collapsed" : ""}`}>
                                {/* Slim strip - click to expand when collapsed */}
                                <div
                                    className="panel-slim-tab"
                                    onClick={() => setIsPanelCollapsed(false)}
                                    title={t("common.expand")}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} className="panel-slim-tab-icon" />
                                    <span className="panel-slim-tab-label">{t("invoice.invoice-details")}</span>
                                    <FontAwesomeIcon icon={faChevronRight} className="panel-slim-tab-chevron" />
                                </div>

                                {/* Full panel content */}
                                <div className="step3-left-panel-header">
                                    <div className="panel-header-content">
                                        <FontAwesomeIcon icon={faFileAlt} />
                                        <span>{t("invoice.invoice-details")}</span>
                                    </div>
                                    <button
                                        className="panel-collapse-btn"
                                        onClick={() => setIsPanelCollapsed(true)}
                                        title={t("common.collapse")}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                </div>
                                <div className="step3-left-body">
                                    <InvoiceDetailFieldsV2 />
                                </div>
                                <InvoiceSummary />
                            </div>

                            {/* Right area - positions center stage */}
                            <div className="step3-right-area">
                                {/* Thin info strip */}
                                <div className="step3-info-strip">
                                    {(mode !== "add" || currentStep === 3) && (
                                        <InvoiceSummaryInfoBar
                                            formValues={formValues}
                                            previousInvoices={previousInvoices}
                                            onShowPreviousInvoices={() => setShowPreviousInvoicesModal(true)}
                                            onShowHistoricalInvoices={() => setShowHistoricalInvoicesModal(true)}
                                            compact
                                        />
                                    )}
                                    {!formValues.readonly && (
                                        <span className="text-muted ms-auto d-flex align-items-center gap-1 inv-autosave-text">
                                            {lastAutosaveTime ? (
                                                <>
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {t("invoice.last-autosaved")}: {lastAutosaveTime}
                                                </>
                                            ) : (
                                                <FontAwesomeIcon icon={faInfoCircle} title={t("invoice.autosave-message")} />
                                            )}
                                            {changeTracking.isDirty && (
                                                <span className="text-warning">
                                                    · {t("invoice.unsaved-changes")}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>

                                {/* Positions - fills remaining height */}
                                <div className="step3-positions-wrapper">
                                    {lvData.loading && (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">{t("common.loading")}</span>
                                            </div>
                                            <p className="mt-2 text-muted">{t("invoice.loading-positions")}</p>
                                        </div>
                                    )}
                                    {!lvData.loading && lvData.lvs.length === 0 && (
                                        <div className="alert alert-info m-3">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                            {t("invoice.no-positions-available")}
                                        </div>
                                    )}
                                    {!lvData.loading && lvData.lvs.length > 0 && (
                                        <HierarchicalPositions />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* All modals */}
            <InvoiceModals
                projectId={projectId}
                formValues={formValues}
                previousInvoices={previousInvoices}
                showConfirmationModal={showConfirmationModal}
                showHistoricalInvoicesModal={showHistoricalInvoicesModal}
                showPreviousInvoicesModal={showPreviousInvoicesModal}
                showPreviousInvoiceHistoricalModal={showPreviousInvoiceHistoricalModal}
                selectedPreviousInvoice={selectedPreviousInvoice}
                onCloseConfirmation={() => setShowConfirmationModal(false)}
                onConfirmHistoricalData={confirmHistoricalData}
                onCloseHistoricalInvoices={() =>
                    setShowHistoricalInvoicesModal(false)
                }
                onClosePreviousInvoices={() =>
                    setShowPreviousInvoicesModal(false)
                }
                onClosePreviousInvoiceHistorical={handleClosePreviousInvoiceHistorical}
                onViewHistoricalInvoices={handleViewHistoricalInvoices}
            />
        </div>
    );
}

export default AddInvoiceV2;
